import { computed, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  DefaultKeyStoreModel,
  KeyStoreModel,
} from "@bitwarden/web-vault/app/models/store/key.store.model";
import { getFiskilConfig } from "@bitwarden/web-vault/app/services/syncing/fiskil/fiskil.config";
import { getPlaidConfig } from "@bitwarden/web-vault/app/services/syncing/plaid/plaid.config";
import { UserStoreService } from "../user/user.store.service";
import {
  fromStoreModel as fromKeyStoreModel,
  fromView as fromKeyView,
  IKeyView,
} from "@bitwarden/web-vault/app/models/view/key/key.view";
import {
  fromStoreModel as fromPluginStoreModel,
  fromView as fromPluginView,
  PluginViewObject,
} from "@bitwarden/web-vault/app/models/view/plugin/plugin.view";
import {
  DefaultPluginStoreModel,
  PluginStoreModel,
} from "@bitwarden/web-vault/app/models/store/plugin.store.model";
import {
  AUSTRALIAN_SUPER_PLUGIN_NAME,
  effectiveAustralianSuperGraphqlUrl,
} from "./australian-super.plugin-config";
import {
  credentialSecretFromForm,
  generatePluginApiKey,
  mapSavedKeyToFormFields,
} from "./plugin-credential-fields";
import { PluginIdentifier, PluginIdentifierType } from "./plugin-identifier";
import {
  AUTO_BUDGET_PLUGIN_DISABLED_KEY,
  AUTO_BUDGET_PLUGIN_ID,
  isToggleOnlyPluginId,
  MONTHLY_SUMMARY_PLUGIN_DISABLED_KEY,
  TOGGLE_ONLY_PLUGIN_DISABLED_KEYS,
  TOGGLE_ONLY_PLUGIN_IDS,
  ToggleOnlyPluginId,
  TRANSACTION_PLUGIN_DISABLED_KEY,
} from "./toggle-only-plugins";

export type PluginType = "buildin" | "plugin";

export { PluginIdentifier };

export {
  AUTO_BUDGET_PLUGIN_DISABLED_KEY,
  AUTO_BUDGET_PLUGIN_ID,
  isToggleOnlyPluginId,
  MONTHLY_SUMMARY_PLUGIN_DISABLED_KEY,
  TOGGLE_ONLY_PLUGIN_DISABLED_KEYS,
  TOGGLE_ONLY_PLUGIN_IDS,
  TRANSACTION_PLUGIN_DISABLED_KEY,
};
export type { ToggleOnlyPluginId };

export const CHATBOT_PLUGIN_DISABLED_KEY = "gloss-chatbot-plugin-disabled";

export { GROCERY_STORES, isGroceryTransaction } from "./transaction-plugin.utils";

export const FISKIL_PLUGIN_NAME = "fiskil";
export const RECURRING_DETECTOR_PLUGIN_ID = "recurring-detector";
// Standalone id (NOT a PluginIdentifier entry): getAllConfigs classifies any name found in
// PluginIdentifier as a "buildin" config, but grocery is a vault-backed "plugin"-type row
// (like recurring-detector), so it must stay out of that map to be read by getConfigsByType("plugin").
export const GROCERY_PLUGIN_NAME = "grocery";
export const WEALTH_COMPARISON_PLUGIN_NAME = "wealth-comparison";
const WEALTH_COMPARISON_PLUGIN_URL = "local://wealth-comparison";
export const PLAID_PLUGIN_NAME = "plaid";
export const CHATBOT_PLUGIN_NAME = "chatbot";
export const BUNDLE_PRICE_LABEL = "Included in Aussie Bank Bundle";

/** Vault plugin-row name for the Transfers & Conversions feature (Phase 5). */
export const TRANSFER_CONVERSION_PLUGIN_NAME = "transfer-conversion";
const TRANSFER_CONVERSION_PLUGIN_URL = "local://transfer-conversion";

/**
 * User-configurable settings for the Transfers & Conversions feature, persisted
 * in the plugin row's `options` blob (vault, so they sync across devices - not
 * localStorage). Absent config, or an absent field within it, reads as the
 * default: the feature ships enabled so existing users get it with no install
 * step.
 */
export interface TransferConversionOptions {
  /** Master switch. Absent config == enabled. */
  enabled: boolean;
  /** Auto-detection window in hours (was the hardcoded 24h `LINK_WINDOW_MS`). */
  linkWindowHours: number;
  /** Whether auto-detection runs at all, independent of the feature being enabled. */
  autoDetectEnabled: boolean;
}

export const DEFAULT_TRANSFER_CONVERSION_OPTIONS: TransferConversionOptions = {
  enabled: true,
  linkWindowHours: 24,
  autoDetectEnabled: true,
};

// Readonly on the interface itself, not just on getConfig()'s return type: TS lets a Readonly<T>
// widen back to T, so per-call-site Readonly<> only held as long as every new annotation,
// parameter or generic kept it. Now anything typed PluginConfig rejects the write. The configs
// getAllConfigs() hands out are memoised and shared with every reader (#5972), and options is the
// deep-frozen PluginView.options, so a write would either leak into other readers or throw at
// runtime; build a new object instead. Still shallow (options' nested values aren't readonly) and
// only a type check: assigning to some other, mutable type isn't caught.
export interface PluginConfig {
  // The type of plugin (e.g., "databank")
  readonly type: PluginType;
  // The URL of the external service
  readonly url: string;
  // The name of the external service, must be unique
  readonly name: string;
  // The API key for authenticating with the external service
  readonly apiKey?: string;
  // Whether the external service is enabled
  readonly enabled: boolean;
  // Optional unique identifier of the key in KeyStoreModel
  readonly keyId?: string;
  // Additional configuration options specific to the plugin type
  readonly options?: Readonly<Record<string, any>>;
}

export const EODHD_PLUGIN_URL = "https://eodhd.plugin.gloss-vault.com/graphql";

const isValidPluginConfig = (config: any): config is PluginConfig => {
  return (
    config &&
    typeof config.type === "string" &&
    typeof config.url === "string" &&
    typeof config.name === "string" &&
    typeof config.enabled === "boolean"
  );
};

@Injectable({
  providedIn: "root",
})
export class PluginConfigService {
  private log = inject(LogService);
  private userStoreService = inject(UserStoreService);

  /**
   * Builds a signal for a plugin that's enabled by default and only ever disabled by an
   * explicit uninstall, tracked via a localStorage flag. Kept as a signal (rather than a plain
   * method) so UI reacting to the toggle updates immediately when it's changed from the
   * Plugins page dialog.
   */
  private persistedPluginEnabledSignal(disabledKey: string) {
    return signal<boolean>(localStorage.getItem(disabledKey) !== "true");
  }

  private setPersistedPluginEnabled(
    disabledKey: string,
    enabledSignal: WritableSignal<boolean>,
    enabled: boolean,
  ): void {
    if (enabled) {
      localStorage.removeItem(disabledKey);
    } else {
      localStorage.setItem(disabledKey, "true");
    }
    enabledSignal.set(enabled);
  }

  /** Chatbot (AI Assistant) — see {@link persistedPluginEnabledSignal}. */
  private chatbotEnabledSignal = this.persistedPluginEnabledSignal(CHATBOT_PLUGIN_DISABLED_KEY);

  /**
   * One enabled signal per TOGGLE_ONLY_PLUGIN_DISABLED_KEYS entry (transaction, monthly
   * summary, ...) — see {@link persistedPluginEnabledSignal}. Keeping these as signals (rather
   * than a plain localStorage read) is what lets OnPush consumers like the side menu's Monthly
   * Summary row react immediately to an install/uninstall from the plugin details dialog,
   * instead of only refreshing on the next navigation (see #4766 follow-up).
   */
  private toggleOnlyPluginEnabledSignals = Object.fromEntries(
    Object.entries(TOGGLE_ONLY_PLUGIN_DISABLED_KEYS).map(([id, key]) => [
      id,
      this.persistedPluginEnabledSignal(key),
    ]),
  ) as Record<ToggleOnlyPluginId, WritableSignal<boolean>>;

  /** All plugin configs. Memoised over the plugin/key collections; the array and its configs are
   * shared between calls, so both are typed readonly. */
  readonly getAllConfigs = computed<readonly PluginConfig[]>(() => {
    const plugins = this.userStoreService.plugin.collection() ?? [];
    const keys = this.userStoreService.key.collection() ?? [];

    return plugins.reduce<PluginConfig[]>((acc, plugin) => {
      const key = keys.find((k) => k.id === plugin.keyId);
      if (key) {
        const type = Object.values<string>(PluginIdentifier).includes(plugin.name)
          ? "buildin"
          : "plugin";
        acc.push({
          type,
          url: plugin.url,
          name: plugin.name,
          enabled: true,
          keyId: plugin.keyId,
          apiKey: key.apiKey,
          options: plugin.options,
        });
      }
      return acc;
    }, []);
  });

  /**
   * Get configurations by plugin type
   */
  getConfigsByType(type: PluginType): PluginConfig[] {
    return this.getAllConfigs().filter((config) => config.type === type);
  }

  /**
   * Get a specific configuration by type and name
   */
  getConfig(name: PluginIdentifierType): PluginConfig | null {
    return this.getAllConfigs().find((config) => config.name === name) || null;
  }

  /**
   * Get saved credentials for a plugin and map them to form field names
   * Returns null if no saved credentials exist
   */
  getSavedCredentials(pluginName: string, formFieldNames: string[]): Record<string, string> | null {
    // Find the saved plugin config
    const savedConfig = this.getConfigsByType("plugin").find((c) => c.name === pluginName);
    if (!savedConfig) {
      return null;
    }

    // Get the saved key (credentials)
    const savedKey = this.userStoreService.key
      .collection()
      ?.find((k) => k.id === savedConfig.keyId);
    if (!savedKey) {
      return null;
    }

    return mapSavedKeyToFormFields(savedKey, formFieldNames);
  }

  /** Whether the user has an enabled Fiskil plugin row in vault (plugin + key). */
  hasEnabledFiskilPluginConfig(): boolean {
    return this.getConfigsByType("plugin").some(
      (config) => config.name === FISKIL_PLUGIN_NAME && config.enabled,
    );
  }

  /**
   * Ensure Fiskil plugin config exists and is enabled after a successful Fiskil redirect.
   * Creates vault key id `fiskil-key-{uuid}` and plugin row when missing.
   */
  async ensureFiskilPluginActivated(): Promise<boolean> {
    if (this.hasEnabledFiskilPluginConfig()) {
      return true;
    }

    const keyId = `fiskil-key-${crypto.randomUUID()}`;
    const keyModel: KeyStoreModel = {
      id: keyId,
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      v: DefaultKeyStoreModel.v,
      vid: null,
      ak: `fiskil-plugin-${keyId}`,
    };

    const savedKey = await this.userStoreService.key.save(fromKeyStoreModel(keyModel));
    if (!savedKey) {
      return false;
    }

    const existingPlugin = this.userStoreService.plugin
      .collection()
      ?.find((p) => p.name === FISKIL_PLUGIN_NAME);

    const pluginView = fromPluginView(
      existingPlugin ?? {
        name: FISKIL_PLUGIN_NAME,
        url: getFiskilConfig().url,
        keyId: savedKey.id,
      },
    );
    pluginView.keyId = savedKey.id;
    return await this.userStoreService.plugin.saveToVault([pluginView], true);
  }

  /** Whether the wealth comparison plugin is installed (plugin row AND matching key both exist). */
  hasWealthComparisonPluginConfig(): boolean {
    return this.getConfigsByType("plugin").some((c) => c.name === WEALTH_COMPARISON_PLUGIN_NAME);
  }

  /**
   * Whether the Grocery plugin is installed (vault plugin row + matching key both exist).
   * Reads from the plugin/key store signals via getConfigsByType, so it is reactive — safe to
   * call inside a computed() (e.g. nav-item gating) and inside the route guard.
   */
  hasGroceryPluginConfig(): boolean {
    return this.getConfigsByType("plugin").some((c) => c.name === GROCERY_PLUGIN_NAME);
  }

  /** Whether the user has an enabled Plaid plugin row in vault (plugin + key). */
  hasEnabledPlaidPluginConfig(): boolean {
    return this.getConfigsByType("plugin").some(
      (config) => config.name === PLAID_PLUGIN_NAME && config.enabled,
    );
  }

  /**
   * Ensure Plaid plugin config exists and is enabled after a successful Plaid link.
   * Creates vault key id `plaid-key-{uuid}` and plugin row when missing.
   */
  async ensurePlaidPluginActivated(): Promise<boolean> {
    if (this.hasEnabledPlaidPluginConfig()) {
      return true;
    }

    const keyId = `plaid-key-${crypto.randomUUID()}`;
    const keyModel: KeyStoreModel = {
      id: keyId,
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      v: DefaultKeyStoreModel.v,
      vid: null,
      ak: `plaid-plugin-${keyId}`,
    };

    const savedKey = await this.userStoreService.key.save(fromKeyStoreModel(keyModel));
    if (!savedKey) {
      return false;
    }

    const existingPlugin = this.userStoreService.plugin
      .collection()
      ?.find((p) => p.name === PLAID_PLUGIN_NAME);

    const pluginView = fromPluginView(
      existingPlugin ?? {
        name: PLAID_PLUGIN_NAME,
        url: getPlaidConfig().url,
        keyId: savedKey.id,
      },
    );
    pluginView.keyId = savedKey.id;
    return await this.userStoreService.plugin.saveToVault([pluginView], true);
  }

  /**
   * Mark the wealth comparison plugin as installed by saving a minimal vault entry.
   * No real credentials needed — the key holds a generated marker string.
   */
  async ensureWealthComparisonPluginActivated(): Promise<boolean> {
    if (this.hasWealthComparisonPluginConfig()) {
      return true;
    }

    const keyId = `wealth-comparison-key-${crypto.randomUUID()}`;
    const keyModel: KeyStoreModel = {
      id: keyId,
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      v: DefaultKeyStoreModel.v,
      vid: null,
      ak: `wealth-comparison-plugin-${keyId}`,
    };

    const savedKey = await this.userStoreService.key.save(fromKeyStoreModel(keyModel));
    if (!savedKey) {
      return false;
    }

    const existingPlugin = this.userStoreService.plugin
      .collection()
      ?.find((p) => p.name === WEALTH_COMPARISON_PLUGIN_NAME);

    const pluginView = fromPluginView(
      existingPlugin ?? {
        name: WEALTH_COMPARISON_PLUGIN_NAME,
        url: WEALTH_COMPARISON_PLUGIN_URL,
        keyId: savedKey.id,
      },
    );
    pluginView.keyId = savedKey.id;
    // saveToVault returns true or throws (never false), so the old `if (!saved) delete key` rollback
    // was dead code — removed (#5633). On a throw the just-minted marker key is left in the vault: we
    // deliberately don't delete it, because deleting a key the server may already have committed is the
    // #5581 risk. At worst one unreferenced marker key leaks per failed attempt — the same accepted
    // tradeoff as upsertConfig's create path and ensureFiskil/ensurePlaid, neither of which rolls back.
    return await this.userStoreService.plugin.saveToVault([pluginView], true);
  }

  /**
   * Add a new configuration
   */
  async upsertConfig(plugin: Omit<PluginViewObject, "keyId">, key: IKeyView): Promise<boolean> {
    const existingPlugin = this.userStoreService.plugin
      .collection()
      ?.find((c) => c.name === plugin.name);

    // Re-save of an existing plugin whose key still exists → update that key IN PLACE, keeping the same
    // id/vid/dc. The plugin row's keyId never changes, so there's no re-point, nothing to delete and
    // nothing to roll back — one vault write instead of three, and no way to strand a key the server
    // already committed on a lost-response save (#5573). fromKeyView mints a fresh random id on every
    // call (key.view.ts), so we overwrite it with the existing key's id/vid/dc to update rather than
    // create a duplicate cipher.
    const existingKey = this.userStoreService.key
      .collection()
      ?.find((k) => k.id === existingPlugin?.keyId);
    if (existingKey) {
      // Merge the incoming fields over the existing key's model so a re-save that leaves username or
      // password blank keeps the stored value instead of wiping it: fromKeyView/toStoreModel omit
      // unm/pwd when the incoming value is blank, and spreading the existing model first lets those
      // omitted fields fall through (#5632). apiKey is always present, so it always overrides. Keep the
      // existing id/vid/dc so the key updates in place rather than creating a duplicate cipher.
      const existing = existingKey.toStoreModel();
      const updated = fromKeyStoreModel({
        ...existing,
        ...fromKeyView(key).toStoreModel(),
        id: existing.id,
        vid: existing.vid,
        dc: existing.dc,
      });
      return await this.userStoreService.key.saveToVault([updated], true);
    }

    // New plugin, or an existing plugin whose key is already missing (repair): create a fresh key and
    // point the row at it. A failed row save here leaks at most one unreferenced key — the pre-#5573
    // behaviour — with no risk of deleting a key the server already committed.
    const res = await this.userStoreService.key.save(fromKeyView(key));
    if (!res) {
      return false;
    }

    const pluginView = fromPluginView(existingPlugin ?? { ...plugin, keyId: res.id });
    pluginView.keyId = res.id;
    return await this.userStoreService.plugin.saveToVault([pluginView], true);
  }

  /**
   * Save plugin credentials from form values
   * Handles mapping between form field names (id, username, password) and stored key fields
   */
  async savePluginCredentials(
    pluginName: string,
    pluginUrl: string,
    formValue: Record<string, string>,
  ): Promise<boolean> {
    const { apiKey: originApiKey, username, id } = formValue;
    const password = credentialSecretFromForm(formValue);

    // Generate API key if not provided (for plugins that use username/password)
    const apiKey = originApiKey || generatePluginApiKey(pluginName);

    // Map 'id' field to 'username' for plugins that use 'id' instead of 'username'
    const usernameToSave = username || id;

    // Validate that we have either API key or username/password (or pin for super)
    if (!apiKey && !(usernameToSave && password)) {
      throw new Error("Either API key or username/password must be provided");
    }

    const urlToSave =
      pluginName === AUSTRALIAN_SUPER_PLUGIN_NAME
        ? effectiveAustralianSuperGraphqlUrl(pluginUrl)
        : pluginUrl;

    // Save to vault using upsertConfig (pin is stored as key password)
    return await this.upsertConfig(
      { url: urlToSave, name: pluginName },
      { apiKey, username: usernameToSave, password },
    );
  }

  /**
   * Remove a plugin configuration by name (vault plugin and associated key).
   */
  async deletePluginByName(name: string): Promise<boolean> {
    try {
      const pluginToDelete = this.userStoreService.plugin
        .collection()
        ?.find((p) => p.name === name);

      if (!pluginToDelete) {
        return false;
      }

      if (pluginToDelete.keyId) {
        const keyToDelete = this.userStoreService.key
          .collection()
          ?.find((k) => k.id === pluginToDelete.keyId);
        if (keyToDelete) {
          await this.userStoreService.key.deleteFromVault([keyToDelete]);
        }
      }

      await this.userStoreService.plugin.deleteFromVault([pluginToDelete]);
      return true;
    } catch (error) {
      this.log.error("Failed to delete plugin config: " + JSON.stringify(error));
      return false;
    }
  }

  /**
   * Delete a plugin configuration
   */
  async deleteConfig(name: PluginIdentifierType): Promise<boolean> {
    return this.deletePluginByName(name);
  }

  /**
   * Validate a configuration string (for backward compatibility)
   */
  validateConfigString(configString: string): boolean {
    try {
      const parsed = JSON.parse(configString);
      return Array.isArray(parsed) && parsed.every(isValidPluginConfig);
    } catch {
      return false;
    }
  }

  /**
   * Whether a toggle-only plugin is active. Enabled by default — only disabled once the user
   * explicitly turns it off from the plugin store. Reactive — safe to read inside a computed()
   * or an OnPush template.
   */
  isToggleOnlyPluginEnabled(pluginId: ToggleOnlyPluginId): boolean {
    // An untyped caller can still pass any id: that reads as "not enabled", not a TypeError inside
    // its computed(). Checked with isToggleOnlyPluginId (own keys), not by indexing the map, which
    // would find Object.prototype members like toString.
    return isToggleOnlyPluginId(pluginId) && this.toggleOnlyPluginEnabledSignals[pluginId]();
  }

  /** Enable/disable a toggle-only plugin, persisting the choice and updating all subscribers. */
  setToggleOnlyPluginEnabled(pluginId: ToggleOnlyPluginId, enabled: boolean): void {
    // Same check as isToggleOnlyPluginEnabled: an unknown id has nothing to persist.
    if (!isToggleOnlyPluginId(pluginId)) {
      return;
    }
    this.setPersistedPluginEnabled(
      TOGGLE_ONLY_PLUGIN_DISABLED_KEYS[pluginId],
      this.toggleOnlyPluginEnabledSignals[pluginId],
      enabled,
    );
  }

  isTransactionPluginEnabled(): boolean {
    return this.isToggleOnlyPluginEnabled(PluginIdentifier.TRANSACTION);
  }

  isMonthlySummaryPluginEnabled(): boolean {
    return this.isToggleOnlyPluginEnabled(PluginIdentifier.MONTHLY_SUMMARY);
  }

  /** Whether the Chatbot (AI Assistant) plugin is active. Reactive — safe to read inside a computed(). */
  isChatbotPluginEnabled(): boolean {
    return this.chatbotEnabledSignal();
  }

  /**
   * Whether a plugin has a vault-backed config, a DATABANK config, an enabled toggle-only
   * flag, or (for the chatbot) its enabled flag. Doesn't cover live-detected installs like
   * the Gloss Vault browser extension — callers with that case must check it themselves.
   */
  isPluginInstalled(pluginId: string): boolean {
    const normalizedId = pluginId?.toLowerCase().replace(/\s/g, "");
    const hasVaultConfig = this.getConfigsByType("plugin").some(
      (config) => config.name?.toLowerCase().replace(/\s/g, "") === normalizedId,
    );
    if (hasVaultConfig) {
      return true;
    }
    if (pluginId === PluginIdentifier.DATABANK) {
      return !!this.getConfig(PluginIdentifier.DATABANK);
    }
    if (isToggleOnlyPluginId(pluginId)) {
      return this.isToggleOnlyPluginEnabled(pluginId);
    }
    if (pluginId === CHATBOT_PLUGIN_NAME) {
      return this.isChatbotPluginEnabled();
    }
    return false;
  }

  /** Enable/disable the Chatbot plugin, persisting the choice and updating all subscribers. */
  setChatbotPluginEnabled(enabled: boolean): void {
    this.setPersistedPluginEnabled(CHATBOT_PLUGIN_DISABLED_KEY, this.chatbotEnabledSignal, enabled);
  }

  /** Whether the Auto-Create Budget plugin is active. Reactive — safe to read inside a computed(). */
  isAutoBudgetPluginEnabled(): boolean {
    return this.isToggleOnlyPluginEnabled(AUTO_BUDGET_PLUGIN_ID);
  }

  /**
   * The Transfers & Conversions settings, merged over the defaults. Reads the
   * plugin row's `options` blob directly (no key join needed), so it works
   * before the plugin has ever been "installed" - a missing row yields the
   * defaults (feature enabled).
   */
  getTransferConversionOptions(): TransferConversionOptions {
    const plugin = this.userStoreService.plugin
      .collection()
      ?.find((p) => p.name === TRANSFER_CONVERSION_PLUGIN_NAME);
    const stored = (plugin?.options ?? {}) as Partial<TransferConversionOptions>;
    const merged = { ...DEFAULT_TRANSFER_CONVERSION_OPTIONS, ...stored };
    // Clamp the auto-detect window on read: findRelatedTransaction's inner break keeps the
    // scan sub-quadratic only while the window is bounded, so a garbage/huge persisted value
    // (or Infinity) would turn every enrichment cycle into an O(n^2) pass. 1h..168h (7 days);
    // non-numeric junk (NaN) falls back to the default.
    return {
      ...merged,
      linkWindowHours: Math.min(
        Math.max(
          Number(merged.linkWindowHours) || DEFAULT_TRANSFER_CONVERSION_OPTIONS.linkWindowHours,
          1,
        ),
        168,
      ),
    };
  }

  /** Master switch for the Transfers & Conversions feature. Defaults to `true`. */
  isTransferConversionEnabled(): boolean {
    return this.getTransferConversionOptions().enabled;
  }

  /**
   * Persist a partial change to the Transfers & Conversions settings, merged
   * over the current values, in the vault plugin row's `options` blob. Creates
   * the row (and a marker key, mirroring the other local plugins) on first use
   * and updates it in place afterwards. Disabling the feature writes
   * `enabled: false` here rather than deleting the row, so manual overrides
   * (`lTid` / `xLid`) on transactions are untouched.
   */
  async saveTransferConversionOptions(
    partial: Partial<TransferConversionOptions>,
  ): Promise<boolean> {
    const merged: TransferConversionOptions = {
      ...this.getTransferConversionOptions(),
      ...partial,
    };

    const existing = this.userStoreService.plugin
      .collection()
      ?.find((p) => p.name === TRANSFER_CONVERSION_PLUGIN_NAME);

    const baseModel: PluginStoreModel = existing
      ? existing.toStoreModel()
      : {
          ...DefaultPluginStoreModel,
          id: crypto.randomUUID(),
          dc: new Date().toISOString(),
          dm: new Date().toISOString(),
          nm: TRANSFER_CONVERSION_PLUGIN_NAME,
          url: TRANSFER_CONVERSION_PLUGIN_URL,
        };

    // No marker key. getAllConfigs() only surfaces a plugin row whose keyId matches a stored
    // key, and getConfigsByType("plugin") reads that list to decide which plugins are
    // installed. A keyless (kid: "") row keeps these settings invisible to the
    // plugin-install surfaces while getTransferConversionOptions() reads options directly.
    // 5.2 should read isTransferConversionEnabled() for install state, never config.enabled.
    // Dropping the key also removes the orphaned-key-on-failure path this method used to carry.
    const model: PluginStoreModel = {
      ...baseModel,
      kid: "",
      opt: JSON.stringify(merged),
    };

    return await this.userStoreService.plugin.saveToVault([fromPluginStoreModel(model)], true);
  }

  static fromString(configString: string): PluginConfig[] | null {
    try {
      return JSON.parse(configString);
    } catch {
      return null;
    }
  }
}
